<template>
  <section class="bx-content bx-carwow bx-typo--carwow"
           :class="{
             [`bx-carwow--${type.toLowerCase()}`]: true,
             'bx-carwow--expired': expired
           }"
           :data-ga-coco-group-type="gaCocoGroupType"
           :data-ga-coco-unique-id="gaCocoUniqueId">
    <div class="bx-affiliate-info">
      <span class="bx-badge bx-badge--affiliate" />
    </div>

    <div v-if="type === 'find'"
         class="bx-carwow__wrapper">
      <Presenter />
      <div class="bx-carwow__headline">
        {{ headline }}
      </div>
      <div class="bx-carwow__pro-image-wrapper">
        <CarImage :type="type"
                  :brand="brand"
                  :image="image" />
        <div class="bx-carwow__pros">
          <div>{{ pros.headline }}</div>
          <ul v-if="pros?.bulletPoints?.length">
            <li v-for="pro in pros.bulletPoints"
                :key="pro">
              <Icon name="seal-check" />
              {{ pro }}
            </li>
          </ul>
        </div>
      </div>
      <form class="bx-form bx-typo--form bx-form--pur bx-typo--form-contact bx-form--carwow"
            @submit.prevent>
        <div class="bx-form__select-wrapper">
          <label class="bx-form__label"
                 for="make">
            Marke
          </label>
          <select id="make"
                  v-model="selectedMake"
                  class="bx-form__select">
            <option disabled
                    value="">
              Bitte auswählen
            </option>
            <option v-for="make in sortedMakes"
                    :key="make.make"
                    :value="make.make">
              {{ make.makeName }}
            </option>
          </select>
          <Icon name="arrow_toggle" />
        </div>
        <div class="bx-form__select-wrapper">
          <label class="bx-form__label"
                 for="modell">
            Modell
          </label>
          <select id="modell"
                  v-model="selectedModel"
                  :disabled="!selectedMake"
                  class="bx-form__select">
            <option disabled
                    value="">
              Bitte auswählen
            </option>
            <option v-for="model in availableModels"
                    :key="model.modelSlug"
                    :value="model.modelSlug">
              {{ model.model }}
            </option>
          </select>
          <Icon name="arrow_toggle" />
        </div>
        <button class="bx-form__button"
                type="button"
                :disabled="!canSubmit"
                :data-href="buyUrl"
                @click="openInNewTab(buyUrl)">
          Barkauf
        </button>
        <button class="bx-form__button"
                type="button"
                :disabled="!canSubmit"
                :data-href="leaseUrl"
                @click="openInNewTab(leaseUrl)">
          Leasing
        </button>
      </form>
    </div>

    <component :is="wrapperTag"
               v-else
               v-bind="singleLinkAttributes()"
               class="bx-carwow__wrapper">
      <div class="bx-carwow__header">
        <span :class="{ 'bx-carwow__badge--empty': !badge }">{{ badge }}</span>
        <span>{{ headline }}</span>
      </div>
      <div class="bx-carwow__subheadline">
        {{ subheadline }}
      </div>
      <div class="bx-carwow__deal">
        <CarImage :type="type"
                  :brand="brand"
                  :image="image" />
        <div v-if="isDuoTeaser"
             class="bx-carwow__duo">
          <a v-bind="singleLinkAttributes('buy')">
            <span class="bx-carwow__duo-topic">Barkauf</span>
            <Prices :type="CarOfferWidgetType.buy"
                    :expired="!buy?.price"
                    :buy="buy" />
            <Cta :show-logo="false" />
          </a>
          <a v-bind="singleLinkAttributes('lease')">
            <span class="bx-carwow__duo-topic">Leasing</span>
            <Prices :type="CarOfferWidgetType.lease"
                    :expired="!lease?.rate"
                    :lease="lease" />
            <Cta :show-logo="false" />
          </a>
        </div>
        <div v-else>
          <Prices :type="type"
                  :expired="expired"
                  :buy="buy"
                  :lease="lease" />
          <Cta />
        </div>
        <Presenter v-if="isDuoTeaser" />
      </div>
    </component>
    <div v-if="disclaimer?.show"
         class="bx-carwow__disclaimer">
      {{ disclaimer.text }}
    </div>
  </section>
</template>

<script>
import { mapState } from 'pinia'

import { CarOfferWidgetType } from '@xcel-next/core/enums'

import { useConfigStore } from '../../stores/config'
import { hashString, ucFirst } from '../../utils/strings.ts'
import Icon from './../shared/Icon.vue'
import Cta from './ParagraphTypeCarOfferWidget/Cta.vue'
import CarImage from './ParagraphTypeCarOfferWidget/CarImage.vue'
import Prices from './ParagraphTypeCarOfferWidget/Prices.vue'
import CarwowLogo from './ParagraphTypeCarOfferWidget/CarwowLogo.vue'
import Presenter from './ParagraphTypeCarOfferWidget/Presenter.vue'

export default {
  name: 'ParagraphTypeCarOfferWidget',
  components: { Icon, Cta, CarImage, Prices, CarwowLogo, Presenter },
  props: {
    type: {
      type: String,
      required: true
    },
    headline: {
      type: String,
      default: ''
    },
    subheadline: {
      type: String,
      default: ''
    },
    badge: {
      type: String,
      default: ''
    },
    car: {
      type: Object,
      default: () => {
        return {}
      }
    },
    pros: {
      type: Object,
      default: () => {
        return {}
      }
    },
    disclaimer: {
      type: Object,
      default: () => {
        return {}
      }
    },
    expired: {
      type: Boolean,
      default: false
    },
    find: {
      type: Object,
      default: () => {
        return {}
      }
    },
    image: {
      type: Object,
      default: () => {
        return {}
      }
    },
    buy: {
      type: Object,
      default: () => {
        return {}
      }
    },
    lease: {
      type: Object,
      default: () => {
        return {}
      }
    },
    // eslint-disable-next-line vue/no-unused-properties
    leasingContractType: {
      type: String,
      default: ''
    },
    // eslint-disable-next-line vue/no-unused-properties
    discountType: {
      type: String,
      default: ''
    },
    brand: {
      type: String,
      default: ''
    },
    pageMeta: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  setup () {
    return { CarOfferWidgetType }
  },
  data () {
    return {
      selectedMake: '',
      selectedModel: ''
    }
  },
  computed: {
    ...mapState(useConfigStore, ['rsConfig']),
    isDuoTeaser () {
      return this.type === 'buyOrLease'
    },
    wrapperTag () {
      return this.isDuoTeaser ? 'div' : 'a'
    },
    sortedMakes () {
      return this?.find?.makes ? this.find.makes.slice().sort((a, b) => a.makeName.localeCompare(b.makeName)) : []
    },
    availableModels () {
      if (this.type !== 'find' || !this?.find?.makes) return []
      const make = this.find.makes.find(m => m.make === this.selectedMake)
      return make ? make.models.slice().sort((a, b) => a.model.localeCompare(b.model)) : []
    },
    canSubmit () {
      return this.selectedMake && this.selectedModel
    },
    buyUrl () {
      return this.buildCarwowUrl('buy')
    },
    leaseUrl () {
      return this.buildCarwowUrl('lease')
    },
    gaCocoGroupType () {
      return `carOffer${ucFirst(this.type)}`
    },
    gaCocoUniqueId () {
      const uniqueString = this.type === 'find'
        ? `${this.type}-${this.pageMeta.nodeId}`
        : `${this.type}-${this.selectedMake}-${this.selectedModel}`
      return hashString(uniqueString)
    }
  },
  watch: {
    selectedMake () {
      if (this.type === 'find') {
        this.selectedModel = ''
      }
    }
  },
  mounted () {
    if (this.type !== 'find') {
      this.selectedMake = this.car.make
      this.selectedModel = this.car.modelSlug
    }
  },
  methods: {
    singleLinkAttributes (forceLink = false) {
      if (this.isDuoTeaser && !forceLink) return {}
      return {
        href: this.type === 'lease' || forceLink === 'lease' ? this.leaseUrl : this.buyUrl,
        target: '_blank',
        rel: 'sponsored noopener'
      }
    },
    /*
      Leasing: https://www.carwow.de/[make]/[model]/leasing?affiliate=azo&utm_medium=editorial&utm_source=affiliate&utm_campaign=af_de_bauer-azo&utm_content=Marke-Modell-Teaser_[Artikel-Node-ID]
      Barkauf: https://www.carwow.de/[make]/[model]?affiliate=azo&utm_medium=editorial&utm_source=affiliate&utm_campaign=af_de_bauer-azo&utm_content=Marke-Modell-Teaser_[Artikel-Node-ID]
    */
    buildCarwowUrl (type) {
      if (this.type === 'find' && !(this.selectedMake && this.selectedModel)) return null

      const base = this.rsConfig.carwow.baseUrl
      const make = this.selectedMake
      const model = this.selectedModel
      const utmContentPrefix = {
        find: 'Marke-Modell',
        buy: 'Barkauf',
        lease: 'Leasing',
        buyOrLease: 'Barkauf-und-Leasing'
      }

      const path = `/${make}/${model}${type === 'lease' ? '/leasing' : ''}`

      const params = new URLSearchParams({
        affiliate: this.rsConfig.carwow.cta.affiliate,
        utm_medium: 'editorial',
        utm_source: 'affiliate',
        utm_campaign: this.rsConfig.carwow.cta.utmCampaign,
        utm_content: `${utmContentPrefix[this.type]}-Teaser_${this.pageMeta.nodeId}`
      })

      return `${base}${path}?${params.toString()}`
    },
    openInNewTab (url) {
      if (!url) return
      window.open(url, '_blank', 'noopener')
    }
  }
}
</script>
